import type { MacroBreakdown, MacroField, NutritionEntry, TargetRange, MinOnlyTarget, MaxOnlyTarget } from '../types/nutrition';

export const MACRO_FIELDS: MacroField[] = ['calories', 'protein', 'carbs', 'fat', 'fibre'];

const ZERO_MACROS: MacroBreakdown = {
  calories: 0,
  protein: 0,
  carbs: 0,
  fat: 0,
  fibre: 0,
};

type LegacyMacroFields = Partial<{
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fat: number;
  fibre: number;
  fibreLegacy: number;
}>;

export function sanitizeMacros(partial?: Partial<MacroBreakdown> | LegacyMacroFields): MacroBreakdown {
  if (!partial) {
    return { ...ZERO_MACROS };
  }

  const resolved: MacroBreakdown = { ...ZERO_MACROS };

  for (const key of MACRO_FIELDS) {
    const value =
      partial[key] ??
      (key === 'fat' ? (partial as LegacyMacroFields).fats ?? (partial as LegacyMacroFields).fat : undefined) ??
      (key === 'fibre' ? (partial as LegacyMacroFields).fibreLegacy : undefined);

    resolved[key] = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  }

  return resolved;
}

export function normalizeNutritionEntry(entry: NutritionEntry & LegacyMacroFields): NutritionEntry {
  if (entry.macros) {
    return {
      ...entry,
      macros: sanitizeMacros(entry.macros),
      hydrationMl: typeof entry.hydrationMl === 'number' ? entry.hydrationMl : 0,
    };
  }

  return {
    ...entry,
    macros: sanitizeMacros(entry),
    hydrationMl: typeof entry.hydrationMl === 'number' ? entry.hydrationMl : 0,
  };
}

export function sumMacros(entries: Array<NutritionEntry | (NutritionEntry & LegacyMacroFields)>): MacroBreakdown {
  return entries.reduce<MacroBreakdown>((acc, entry) => {
    const normalized = normalizeNutritionEntry(entry);
    return {
      calories: acc.calories + normalized.macros.calories,
      protein: acc.protein + normalized.macros.protein,
      carbs: acc.carbs + normalized.macros.carbs,
      fat: acc.fat + normalized.macros.fat,
      fibre: acc.fibre + normalized.macros.fibre,
    };
  }, { ...ZERO_MACROS });
}

export function sumHydration(entries: NutritionEntry[]): number {
  return entries.reduce((total, entry) => total + (entry.hydrationMl || 0), 0);
}

export function formatTargetRange(range?: TargetRange | MinOnlyTarget | MaxOnlyTarget | null, unit = ''): string | null {
  if (!range) return null;
  const hasMin = typeof range.min === 'number' && Number.isFinite(range.min);
  const hasMax = typeof (range as TargetRange | MaxOnlyTarget).max === 'number' && Number.isFinite((range as TargetRange | MaxOnlyTarget).max);

  if (hasMin && hasMax) {
    const min = range.min!;
    const max = (range as TargetRange).max!;
    if (max > min * 20 || max === min) {
      return `Min: ${Math.round(min)}${unit ? ` ${unit}` : ''}`;
    }
    return `${Math.round(min)}–${Math.round(max)}${unit ? ` ${unit}` : ''}`;
  }

  if (hasMin) {
    return `Min: ${Math.round(range.min!)}${unit ? ` ${unit}` : ''}`;
  }

  if (hasMax) {
    return `Max: ${Math.round((range as MaxOnlyTarget).max!)}${unit ? ` ${unit}` : ''}`;
  }

  return null;
}

export function deriveRangeFromTarget(target: number, spread: number): TargetRange {
  return {
    target,
    min: Math.max(0, target - spread),
    max: Math.max(target + spread, target),
  };
}

/**
 * Try to parse a macro string provided by the user.
 * Returns null if no macro tokens found, otherwise returns description and macros.
 *
 * SINGLE-PASS TOKENISER — order-agnostic, no cross-match conflicts.
 *
 * Supported formats (both orders):
 *   "Chicken curry 421 kcal 45g protein 32g carbs 22g fat 4g fibre"
 *   "protein 40g carbs 50g fat 15g calories 480"
 *   "480 kcal – 40g protein, 50g carbs, 15g fat"
 *   "5g creatine"
 *   "10g collagen"
 *   "5g creatine + 10g collagen"
 *
 * Rules:
 *   - No single-letter aliases (p/c/f) — too many false positives
 *   - Macros REQUIRE 'g' unit (e.g. "40g protein" or "protein 40g")
 *   - Calories require kcal/cal/cals/calories label
 *   - Fibre/creatine/collagen also require explicit label
 *   - Creatine and collagen count toward explicitFieldCount so supplement-only
 *     inputs are not rejected
 *   - Each token is consumed once; no double-matching
 */
export function tryParseInlineMacroString(
  input: string,
): { description: string; macros: MacroBreakdown; creatine?: number; collagen?: number; explicitFieldCount: number } | null {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // ── Token definitions ──
  // Order matters: longer labels first to prevent partial matches (e.g. "fibre" before "fat")
  const tokenDefs: Array<{ field: string; patterns: RegExp[] }> = [
    {
      field: 'calories',
      patterns: [
        /(\d+(?:\.\d+)?)\s*(?:kcal|cals?|calories)\b/i,
        /\b(?:kcal|cals?|calories)\s*[:=]?\s*(\d+(?:\.\d+)?)/i,
      ],
    },
    {
      field: 'fibre',
      patterns: [
        /(\d+(?:\.\d+)?)\s*g?\s*(?:fibre|fiber)\b/i,
        /\b(?:fibre|fiber)\s*(\d+(?:\.\d+)?)\s*g?/i,
      ],
    },
    {
      field: 'collagen',
      patterns: [
        /(\d+(?:\.\d+)?)\s*g?\s*collagen\b/i,
        /\bcollagen\s*(\d+(?:\.\d+)?)\s*g?/i,
      ],
    },
    {
      field: 'creatine',
      patterns: [
        /(\d+(?:\.\d+)?)\s*g?\s*creatine\b/i,
        /\bcreatine\s*(\d+(?:\.\d+)?)\s*g?/i,
      ],
    },
    {
      field: 'protein',
      patterns: [
        /(\d+(?:\.\d+)?)\s*g\s*(?:protein)\b/i,
        /\b(?:protein)\s*(\d+(?:\.\d+)?)\s*g/i,
      ],
    },
    {
      field: 'carbs',
      patterns: [
        /(\d+(?:\.\d+)?)\s*g\s*(?:carbs|carbohydrates)\b/i,
        /\b(?:carbs|carbohydrates)\s*(\d+(?:\.\d+)?)\s*g/i,
      ],
    },
    {
      field: 'fat',
      patterns: [
        /(\d+(?:\.\d+)?)\s*g\s*(?:fats?)\b/i,
        /\b(?:fats?)\s*(\d+(?:\.\d+)?)\s*g/i,
      ],
    },
  ];

  // ── Single-pass extraction ──
  const extracted: Record<string, number> = {};
  let remaining = trimmed;
  let explicitFieldCount = 0;

  for (const { field, patterns } of tokenDefs) {
    if (extracted[field] !== undefined) continue;

    for (const pattern of patterns) {
      const match = remaining.match(pattern);
      if (match) {
        const value = parseFloat(match[1]);
        if (Number.isFinite(value) && value >= 0) {
          extracted[field] = value;
          remaining = remaining.replace(match[0], ' ').trim();
          // Creatine and collagen count — supplement-only inputs must not return null
          explicitFieldCount++;
          break;
        }
      }
    }
  }

  // ── Must have found at least one token ──
  if (explicitFieldCount === 0) return null;

  // ── Clean up description ──
  const description = remaining
    .replace(/[-–—:=;,+]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .replace(/^\s+|\s+$/g, '')
    || 'Supplement';

  return {
    description,
    macros: sanitizeMacros({
      calories: extracted.calories,
      protein: extracted.protein,
      carbs: extracted.carbs,
      fat: extracted.fat,
      fibre: extracted.fibre,
    }),
    creatine: extracted.creatine !== undefined ? extracted.creatine : undefined,
    collagen: extracted.collagen !== undefined ? extracted.collagen : undefined,
    explicitFieldCount,
  };
}
